import {useMemo, useState} from 'react';
import {SVG} from '../../../../../../../constants/assetLists';
import {useSnackbar} from '../../../../../../../context/SnackbarContextProvider';
import Switcher from '../../Switcher';
import {NetworkDetailsFormValues, NetworkNodeFieldPath} from '../../../types';
import {useFieldArray, useFormContext} from 'react-hook-form';
import {useOnBoardingContext} from '../../../../../../../context/onBoarding/OnBoardingContextProvider';
import AppConfirmationDialog from '../../../../../../../components/common/AppConfirmationDialog';
import AppMenu from '../../../../../../../components/common/AppMenu';
import AppButton from '../../../../../../../components/common/AppButton';
import {getManualShifts, getTemplateShifts} from './defaultShift';
import {
  cloneShiftsFromSource,
  collectShiftSourceOptions,
} from './shiftCopyUtils';
import {canReplaceShifts} from './shiftReferenceUtils';
import OnboardingValidationBadge from '../../OnboardingValidationBadge';
import { get } from 'react-hook-form';
type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
};

const SHIFTS = [
  {
    name: 'כל תחילת יום',
    desc: 'בכל תחילת יום יתאפסו ההערות והקבצים שצורפו לצ’קליסט ביום קודם',
  },
  {
    name: 'כל סוף יום',
    desc: 'בכל סוף יום יתאפסו ההערות והקבצים שצורפו לצ’קליסט באותו היום',
  },
  {
    name: 'בשעה ספציפית',
    desc: 'ניתן לבחור שעה ספציפית בה יתאפסו ההערות והקבצים שצורפו לצ’קליסט',
  },
];

function ShiftsHeader({selectedFieldPath}: Props) {
  const {getDefaultSelectedNode, setDefaultSelectedNodeForPath} =
    useOnBoardingContext();
  const defaultSelectedNode = getDefaultSelectedNode(selectedFieldPath);
  const {showSnackbar} = useSnackbar();

  const [pendingShift, setPendingShift] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showShiftsDetails, setShowShiftsDetails] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedSourceName, setSelectedSourceName] = useState<string | null>(
    null,
  );
  const [pendingSourcePath, setPendingSourcePath] =
    useState<NetworkNodeFieldPath | null>(null);
  const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);

  const {control, setValue, watch, getValues,formState: { errors }} =
    useFormContext<NetworkDetailsFormValues>();
    const shifts = watch(`${selectedFieldPath}.shifts`) ?? [];
const shiftsError = get(
  errors,
  `${selectedFieldPath}.shifts`
);
const showshiftsValidation =
  shifts.length === 0 ||
  !!shiftsError;
  const {replace} = useFieldArray({
    control,
    name: `${selectedFieldPath}.shifts`,
    keyName: 'id',
  });

  const network = watch('network') ?? [];

  const blockReplaceIfShiftsInUse = (): boolean => {
    const result = canReplaceShifts(network, selectedFieldPath);
    if (!result.allowed) {
      showSnackbar({
        message: result.message,
        type: 'error',
        duration: 5000,
      });
      return true;
    }
    return false;
  };

  const menuItems = useMemo(
    () =>
      collectShiftSourceOptions(network, selectedFieldPath).map(o => ({
        title: o.title,
        value: o.value,
      })),
    [network, selectedFieldPath],
  );

  const buttonLabel = selectedSourceName
    ? `ייבוא הגדרות מ${selectedSourceName}`
    : 'ייבוא הגדרות מ...';

  const getShiftsForMode = (mode: string) =>
    mode === 'manual' ? getManualShifts() : getTemplateShifts();

  const handleSwitcherChange = (id: string) => {
    if (id === defaultSelectedNode.shift) return;
    if (blockReplaceIfShiftsInUse()) return;

    setPendingShift(id);
    setShowConfirmation(true);
  };

  const handleConfirmChange = () => {
    if (!pendingShift) return;
    if (blockReplaceIfShiftsInUse()) {
      handleCloseConfirmation();
      return;
    }

    const newShifts = getShiftsForMode(pendingShift);
    replace(newShifts);
    setValue(`${selectedFieldPath}.shifts`, newShifts, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue(`${selectedFieldPath}.roles`, []);
    setDefaultSelectedNodeForPath(selectedFieldPath, prev => ({
      ...prev,
      shift: pendingShift,
    }));
    setShowConfirmation(false);
    setPendingShift(null);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setPendingShift(null);
  };

  const toggleShiftsDetails = (opt: boolean) => {
    setShowShiftsDetails(opt);
  };

  const handleSourceSelect = (sourcePath: string) => {
    if (blockReplaceIfShiftsInUse()) return;

    const sourceName =
      getValues(
        `${sourcePath as NetworkNodeFieldPath}.name` as const,
      )?.trim() || 'ללא שם';

    setSelectedSourceName(sourceName);
    setPendingSourcePath(sourcePath as NetworkNodeFieldPath);
    setShowCopyConfirmation(true);
  };

  const handleCloseCopyConfirmation = () => {
    setShowCopyConfirmation(false);
    setPendingSourcePath(null);
    setSelectedSourceName(null);
  };

  const handleConfirmCopy = () => {
    if (!pendingSourcePath) return;
    if (blockReplaceIfShiftsInUse()) {
      handleCloseCopyConfirmation();
      return;
    }

    const sourceShifts =
      getValues(`${pendingSourcePath}.shifts` as const) ?? [];
    const cloned = cloneShiftsFromSource(sourceShifts);

    replace(cloned);
    setValue(`${selectedFieldPath}.shifts`, cloned, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue(`${selectedFieldPath}.checklists`, []);
    setDefaultSelectedNodeForPath(selectedFieldPath, prev => ({
      ...prev,
      shift: 'manual',
    }));
    setShowCopyConfirmation(false);
    setPendingSourcePath(null);
  };

  const subtitle =
    pendingShift === 'manual' ? (
      <span>
        מעבר <b>ידנית</b> יאפס את ההגדרות בתבנית שהוגדרו. האם להמשיך?
      </span>
    ) : (
      <span>
        מעבר <b>לתבנית</b> יאפס את ההגדרות האישיות שהוגדרו. האם להמשיך?
      </span>
    );

  const copySubtitle = (
    <p>
      העתקת משמרות מ-
      <span className="font-bold">{selectedSourceName || 'ללא שם'}</span> תחליף
      את המשמרות הנוכחיות ותאפס את התפקידים. האם להמשיך?
    </p>
  );

  return (
    <>
      <div className="pb-2">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <p className="m-0 text-xl text-deepAqua font-bold">הגדרת משמרות</p>
              <OnboardingValidationBadge show={showshiftsValidation}  />
            </div>
            <p className="m-0 text-base text-darkerGray">
              הגדירו את המשמרות בארגונכם
            </p>
          </div>

          <div>
            <Switcher
              active={defaultSelectedNode.shift}
              onChange={handleSwitcherChange}
            />
          </div>

          <div className="flex justify-center md:justify-end">
            <AppMenu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onOpen={e => setMenuAnchorEl(e.currentTarget as HTMLElement)}
              onClose={() => setMenuAnchorEl(null)}
              items={menuItems}
              disableMenuIcon={menuItems.length === 0}
              onItemClick={handleSourceSelect}
              minWidth={260}
              menuIcon={
                <AppButton
                  variant="primary"
                  disabled={menuItems.length === 0}
                  iconEnd={
                    <img
                      src={SVG.dropdownWhiteIcon}
                      alt="icon"
                      className="w-[12px] h-[6px] object-contain shrink-0"
                    />
                  }>
                  {buttonLabel}
                </AppButton>
              }
            />
          </div>
        </div>

        <div className="mt-1 flex flex-col gap-2.5">
          <div className="flex justify-end">
            <button
              className="py-1 px-2 bg-icyBlue rounded-md flex items-center gap-2"
              onClick={() => toggleShiftsDetails(true)}>
              <img
                src={SVG.infoBlueIcon}
                alt="info"
                className="w-5 h-5 object-contain"
              />
              <p className="m-0 text-base text-tealBlue underline">
                מה זה איפוס הערות בצ’קליסטים
              </p>
            </button>
          </div>

          {showShiftsDetails && (
            <div className="px-4 py-1 border border-[#E9FAFF] bg-[#F0FCFF] rounded-tr-md rounded-br-md rounded-bl-md shadow-md relative">
              <p className="m-0 text-base text-black mb-2">
                במהלך יום העבודה ניתן לצרף הערות וקבצים לצ'קליסטים השוטפים. בכל
                יום המערכת מאפסת את ההערות והקבצים באופן אוטומטי – באפשרותכם
                לבחור את שעת האיפוס המועדפת עליכם.
              </p>

              {SHIFTS.map((shft, idx) => (
                <div
                  key={idx}
                  className={`py-1 flex items-center gap-2 border-b border-gray70 ${idx === SHIFTS.length - 1 ? 'border-b-0' : ''}`}>
                  <div className="w-[122px]">
                    <p className="m-0 text-base font-bold text-darkBlue">
                      {shft.name}
                    </p>
                  </div>

                  <p className="m-0 text-base text-black">{shft.desc}</p>
                </div>
              ))}

              <button
                className="absolute top-1 left-2 w-6 h-6 flex justify-center items-center pr-3"
                onClick={() => toggleShiftsDetails(false)}>
                <img
                  src={SVG.closeDarkIcon}
                  className="w-[11px] h-[11px] object-contain"
                />
              </button>
            </div>
          )}
        </div>
      </div>

      <AppConfirmationDialog
        open={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmChange}
        icon={
          <img src={SVG.infoBlueIcon} alt="" className="w-[48px] h-[48px]" />
        }
        title="שימו לב"
        subTitle={subtitle}
        confirmBtnText="כן"
        cancelBtnText="לא"
        cancelBtnTextClasses="!text-raspberryRed"
        bordered={false}
      />

      <AppConfirmationDialog
        open={showCopyConfirmation}
        onClose={handleCloseCopyConfirmation}
        onConfirm={handleConfirmCopy}
        icon={
          <img src={SVG.infoBlueIcon} alt="" className="w-[48px] h-[48px]" />
        }
        title="שימו לב"
        subTitle={copySubtitle}
        confirmBtnText="כן"
        cancelBtnText="לא"
        cancelBtnTextClasses="!text-raspberryRed"
        bordered={false}
      />
    </>
  );
}

export default ShiftsHeader;
